import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ActivityFeedItem, ActivityPeriod, ActivityType } from '../models/activity-feed.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ActivityFeedService {
  private apiServerUrl = environment.apiBaseUrl;
  
  constructor(private http: HttpClient) { }

  getCampaignFeed(quizId: number, period: ActivityPeriod, types?: ActivityType[]): Observable<ActivityFeedItem[]> {
    let params = new HttpParams()
        .set('quizId', quizId.toString())
        .set('period', period);

    if (types && types.length > 0) {
      params = params.set('types', types.join(','));
    }

    return this.http.get<ActivityFeedItem[]>(`${this.apiServerUrl}/activity-feed`, { params });
  }

  getCandidateFeed(
    candidateId: number, 
    period: ActivityPeriod, 
    types?: ActivityType[]
  ): Observable<ActivityFeedItem[]> {
    let params = new HttpParams().set('period', period);

    if (types && types.length > 0) {
      params = params.set('types', types.join(','));
    }

    return this.http.get<ActivityFeedItem[]>(
      `${this.apiServerUrl}/activity-feed/candidate/${candidateId}`,
      { params }
    );  
  }
    public getCompanyFeed(
    companyId: number,
    period: ActivityPeriod,
    types?: ActivityType[]
  ): Observable<ActivityFeedItem[]> {
    let params = new HttpParams()
      .set('companyId', companyId.toString())
      .set('period', period);

    if (types && types.length > 0) {
      params = params.set('types', types.join(','));
    }

    return this.http.get<ActivityFeedItem[]>(
      `${this.apiServerUrl}/activity-feed`,
      { params }
    );
  }

  public getActivityIcon(type: string): string {
    const iconMap: Record<string, string> = {
      [ActivityType.CANDIDATE_APPLIED]: 'person_add',
      [ActivityType.CANDIDATE_MOVED]: 'arrow_forward',
      [ActivityType.COMMENT_ADDED]: 'comment',
      [ActivityType.DESCRIPTION_UPDATED]: 'edit',
      [ActivityType.CANDIDATE_UPDATED]: 'upload_file',
      [ActivityType.INTERVIEW_BOOKED]: 'event',
      [ActivityType.INTERVIEW_CONFIRMED]: 'event_available',
      [ActivityType.INTERVIEW_CANCELLED]: 'event_busy',
      [ActivityType.INTERVIEW_COMPLETED]: 'check_circle',
      [ActivityType.INTERVIEW_NO_SHOW]: 'person_off',
      [ActivityType.INTERVIEW_REMINDER_SENT]: 'notifications',
      [ActivityType.ATTENDANCE_CONFIRMED]: 'how_to_reg',
      [ActivityType.ATTENDANCE_DECLINED]: 'person_remove',
      [ActivityType.WHATSAPP_SENT]: 'send',
      [ActivityType.WHATSAPP_RECEIVED]: 'message',
      [ActivityType.TEMPLATE_SENT]: 'mail',
      [ActivityType.FOLLOW_UP_REMINDER_SENT]: 'notification_important',
      [ActivityType.WORKFLOW_STARTED]: 'play_circle',
      [ActivityType.SECOND_QUIZ_COMPLETED]: 'quiz'
    };
    return iconMap[type] || 'info';
  }

  public getActivityColor(type: string): string {
    const colorMap: Record<string, string> = {
      [ActivityType.CANDIDATE_APPLIED]: '#4caf50',
      [ActivityType.CANDIDATE_MOVED]: '#2196f3',
      [ActivityType.COMMENT_ADDED]: '#ff9800',
      [ActivityType.DESCRIPTION_UPDATED]: '#9e9e9e',
      [ActivityType.CANDIDATE_UPDATED]: '#42a5f5',
      [ActivityType.INTERVIEW_BOOKED]: '#2196f3',
      [ActivityType.INTERVIEW_CONFIRMED]: '#4caf50',
      [ActivityType.INTERVIEW_CANCELLED]: '#f44336',
      [ActivityType.INTERVIEW_COMPLETED]: '#4caf50',
      [ActivityType.INTERVIEW_NO_SHOW]: '#f44336',
      [ActivityType.INTERVIEW_REMINDER_SENT]: '#ff9800',
      [ActivityType.ATTENDANCE_CONFIRMED]: '#4caf50',
      [ActivityType.ATTENDANCE_DECLINED]: '#f44336',
      [ActivityType.WHATSAPP_SENT]: '#25D366',
      [ActivityType.WHATSAPP_RECEIVED]: '#25D366',
      [ActivityType.TEMPLATE_SENT]: '#9c27b0',
      [ActivityType.FOLLOW_UP_REMINDER_SENT]: '#ff9800',
      [ActivityType.WORKFLOW_STARTED]: '#2196f3',
      [ActivityType.SECOND_QUIZ_COMPLETED]: '#4caf50'
    };
    return colorMap[type] || '#9e9e9e';
  }

  public getSummaryKey(activityType: string): string {
    return `activityFeed.summary.${activityType}`;
  }

  public getSummaryParams(activity: ActivityFeedItem): Record<string, string> {
    let params: Record<string, string> = {
      candidateName: activity.candidateName || 'Unknown',
      actorName: activity.actorName || 'System',
      quizTitle: activity.quizTitle || ''
    };

    if (activity.details) {
      try {
        const details = JSON.parse(activity.details);
        params = { ...params, ...details };
      } catch {
        // Invalid JSON, use defaults
      }
    }

    return params;
  }

  public getActivityDetail(activity: ActivityFeedItem): string | null {
    if (!activity.details) return null;

    try {
      const details = JSON.parse(activity.details);

      switch (activity.activityType) {
        case ActivityType.CANDIDATE_MOVED:
          return `${details.fromStage} → ${details.toStage}`;
        case ActivityType.COMMENT_ADDED:
          const text = details.commentText || '';
          return text.length > 80 ? text.substring(0, 80) + '...' : text;
        case ActivityType.INTERVIEW_BOOKED:
          const timeRange = details.endTime
            ? `${details.startTime}-${details.endTime}`
            : details.startTime;
          return `${details.slotDate} ${timeRange}`;
        case ActivityType.CANDIDATE_APPLIED:
          return `Score: ${details.score} (${details.percentage})`;
        default:
          return null;
      }
    } catch {
      return null;
    }
  }
}
